
import { z } from 'zod';

export const ROLES = ['agent', 'admin', 'billing', 'owner'] as const;
export type Role = (typeof ROLES)[number];

export const RoleSchema = z.enum(ROLES);

export const RolesSchema = z
  .array(RoleSchema)
  .min(1, 'Select at least one role.')
  .refine((roles) => new Set(roles).size === roles.length, {
    message: 'Roles must be unique.',
  });

export const OwnerRolesSchema = RolesSchema.refine((roles) => roles.includes('owner'), {
  message: 'The owner role is required.',
});

export const DEFAULT_OWNER_ROLES: readonly Role[] = ['owner'];

export const NameSchema = z
  .string()
  .trim()
  .min(1, 'A name is required.')
  .max(120, 'That name is too long.');


export const FirstNameSchema = z
  .string()
  .trim()
  .min(1, 'A first name is required.')
  .max(60, 'That first name is too long.');

export const LastNameSchema = z
  .string()
  .trim()
  .min(1, 'A last name is required.')
  .max(60, 'That last name is too long.');

export function combineName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
}

export function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const [first = '', ...rest] = parts;
  return { firstName: first, lastName: rest.join(' ') };
}


export const EMAIL_MAX_LENGTH = 254;

export const EmailSchema = z
  .string()
  .trim()
  .min(1, 'An email is required.')
  .max(EMAIL_MAX_LENGTH, 'That email is too long.')
  .email('Enter a valid email address.')
  .toLowerCase();

export const InviteUserInputSchema = z.object({
  email: EmailSchema,
  name: NameSchema,
  roles: RolesSchema,
});

export type InviteUserInput = z.infer<typeof InviteUserInputSchema>;

export const InviteUserFormSchema = z.object({
  email: EmailSchema,
  firstName: FirstNameSchema,
  lastName: LastNameSchema,
  roles: RolesSchema,
});

export type InviteUserFormInput = z.infer<typeof InviteUserFormSchema>;

export const EMPTY_INVITE_FORM: InviteUserFormInput = {
  email: '',
  firstName: '',
  lastName: '',
  roles: ['agent'],
};

export const UpdateUserRolesInputSchema = z.object({
  userId: z.string().uuid('A valid user id is required.'),
  roles: RolesSchema,
});

export type UpdateUserRolesInput = z.infer<typeof UpdateUserRolesInputSchema>;

export const TransferOwnershipInputSchema = z.object({
  toUserId: z.string().uuid('A valid user id is required.'),
});

export type TransferOwnershipInput = z.infer<typeof TransferOwnershipInputSchema>;

export const SetUserActiveInputSchema = z.object({
  userId: z.string().uuid('A valid user id is required.'),
  active: z.boolean(),
});

export type SetUserActiveInput = z.infer<typeof SetUserActiveInputSchema>;

export const UpdateMyProfileInputSchema = z.object({
  name: NameSchema,
});

export type UpdateMyProfileInput = z.infer<typeof UpdateMyProfileInputSchema>;

export const UpdateMyProfileFormSchema = z.object({
  firstName: FirstNameSchema,
  lastName: LastNameSchema,
});

export type UpdateMyProfileFormInput = z.infer<typeof UpdateMyProfileFormSchema>;
